import { ApiError } from "@/common/lib/api";
import { APPRECIATION_ENABLED } from "@/common/lib/appreciationEnabled";
import {
  type AccountingFramework,
  useUpdateAccountDefaults,
  useUpdateAccountMeta,
} from "@/features/accounts/api/accountsApi";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { useMappingsList } from "@/features/journal/api/journalApi";
import { ChevronRight, Lock } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const FRAMEWORK_OPTIONS: { value: AccountingFramework; label: string; hint: string }[] = [
  { value: "pymes_s34", label: "NIIF para PYMES — Sección 34", hint: "Default Costa Rica" },
  { value: "ifrs_full", label: "NIIF plenas", hint: "Requiere auditor" },
];

export function AjustesPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const role = currentMembership?.role ?? "viewer";
  const canEditMeta = role === "owner";
  const canEditDefaults = role === "owner" || role === "admin";

  if (!currentAccount) {
    return <SkeletonAjustes />;
  }

  return (
    <>
      <title>Ajustes · HATO</title>
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 md:px-6">
        <header>
          <h1 className="font-semibold text-2xl text-app-ink-1 tracking-tight">
            Ajustes de la cuenta
          </h1>
          <p className="mt-1 text-app-ink-3 text-sm">
            Configurá nombre, marco contable y defaults de depreciación.
          </p>
        </header>

        {role === "viewer" && (
          <div className="rounded-xl border border-app-border bg-app-surface-2 px-4 py-3 text-app-ink-2 text-sm">
            Tu rol es <strong>viewer</strong>: ves la configuración en solo lectura.
          </div>
        )}

        <MetaCard
          accountId={currentAccount.id}
          initialName={currentAccount.name}
          initialCountry={currentAccount.countryCode}
          initialFramework={currentAccount.accountingFramework}
          editable={canEditMeta}
        />

        <DefaultsCard
          accountId={currentAccount.id}
          initialUsefulLife={currentAccount.defaultUsefulLifeMonths}
          initialResidualRatio={Number.parseFloat(currentAccount.defaultResidualValueRatio)}
          editable={canEditDefaults}
        />

        <ChartCard editable={canEditDefaults} />
        {/* Apreciación apagada en v1 (apreciacion-apagada-v1.md): la tarjeta de
            Valores referenciales se oculta (la de Defaults de depreciación se
            queda). Revertir = flipar APPRECIATION_ENABLED en
            common/lib/appreciationEnabled.ts. */}
        {APPRECIATION_ENABLED && <ReferenceValuesCard editable={canEditDefaults} />}
        <MappingsCard accountId={currentAccount.id} editable={canEditDefaults} />
      </div>
    </>
  );
}

// ─── Card 4c: Valores referenciales de apreciación (Sprint 3) ──────────────
// Entry point a /app/ajustes/valores-referenciales. Tabla A (valor por
// categoría/fase de lactancia) + Tabla B (precio por kg).

function ReferenceValuesCard({ editable }: { editable: boolean }) {
  const ctaLabel = editable ? "Configurar valores" : "Ver valores";

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-app-ink-1 text-base">Valores referenciales</h2>
          <p className="mt-0.5 text-app-ink-3 text-xs">
            Rangos de valor por categoría y precios por kg usados por el cálculo mensual de
            apreciación. Para vacas en producción se distingue por fase de lactancia.
          </p>
        </div>
        {!editable && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-app-surface-2 px-2 py-0.5 text-app-ink-3 text-xs"
            aria-label="Solo lectura"
          >
            <Lock className="h-3 w-3" />
            Solo lectura
          </span>
        )}
      </header>

      <div className="flex justify-end">
        <Link
          to="/app/ajustes/valores-referenciales"
          className="inline-flex items-center gap-1 self-start rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d]"
        >
          {ctaLabel}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

// ─── Card 4a: Plan de cuentas (Sprint 1) ────────────────────────────────────
// Entry point a /app/ajustes/plan-de-cuentas. La pantalla completa hace el
// CRUD del árbol contable jerárquico.

function ChartCard({ editable }: { editable: boolean }) {
  const ctaLabel = editable ? "Administrar plan" : "Ver plan";

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-app-ink-1 text-base">Plan de cuentas</h2>
          <p className="mt-0.5 text-app-ink-3 text-xs">
            Árbol contable jerárquico de tu finca (Activos / Pasivos / Patrimonio / Ingresos /
            Gastos / Costos). Podés renombrar las cuentas del sistema y agregar sub-cuentas tuyas.
          </p>
        </div>
        {!editable && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-app-surface-2 px-2 py-0.5 text-app-ink-3 text-xs"
            aria-label="Solo lectura"
          >
            <Lock className="h-3 w-3" />
            Solo lectura
          </span>
        )}
      </header>

      <div className="flex justify-end">
        <Link
          to="/app/ajustes/plan-de-cuentas"
          className="inline-flex items-center gap-1 self-start rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d]"
        >
          {ctaLabel}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

// ─── Card 4b: Mapeo contable (B43) ───────────────────────────────────────────
// Resumen + acceso a /app/ajustes/mapeo-cuentas. La pantalla completa hace el
// CRUD; esta card solo es el entry point visible para que no quede oculta.
//
// Sprint 1 (2026-05-16): "configured" cuenta las cuentas del sistema con
// override del cliente. El total son las cuentas del sistema del árbol.

function MappingsCard({ accountId, editable }: { accountId: string; editable: boolean }) {
  const mappingsQuery = useMappingsList(accountId);
  const systemRows = (mappingsQuery.data?.mappings ?? []).filter((m) => m.isSystem);
  const total = systemRows.length;
  const configured = systemRows.filter((m) => m.overrideId !== null).length;
  const missing = Math.max(total - configured, 0);
  const isLoading = mappingsQuery.isLoading;
  const ctaLabel = editable ? "Configurar mapeo" : "Ver mapeo";

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-app-ink-1 text-base">Mapeo contable</h2>
          <p className="mt-0.5 text-app-ink-3 text-xs">
            Traducción entre las cuentas internas de MiHato y el plan contable de tu finca. Se usa
            al generar los asientos contables del mes.
          </p>
        </div>
        {!editable && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-app-surface-2 px-2 py-0.5 text-app-ink-3 text-xs"
            aria-label="Solo lectura"
          >
            <Lock className="h-3 w-3" />
            Solo lectura
          </span>
        )}
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-app-ink-2 text-sm" data-testid="mappings-status">
          {isLoading ? (
            <span className="text-app-ink-3">Cargando estado del mapeo…</span>
          ) : (
            <>
              <strong className="font-medium text-app-ink-1">
                {configured} de {total}
              </strong>{" "}
              cuentas configuradas
              {missing > 0 ? <span className="text-app-ink-3"> · {missing} pendientes</span> : null}
            </>
          )}
        </p>
        <Link
          to="/app/ajustes/mapeo-cuentas"
          className="inline-flex items-center gap-1 self-start rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d]"
        >
          {ctaLabel}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

// ─── Card 1+2: Datos de la finca + marco contable (PATCH /:accountId) ────────

function MetaCard(props: {
  accountId: string;
  initialName: string;
  initialCountry: string;
  initialFramework: AccountingFramework;
  editable: boolean;
}) {
  const mutation = useUpdateAccountMeta(props.accountId);
  const [name, setName] = useState(props.initialName);
  const [countryCode, setCountryCode] = useState(props.initialCountry);
  const [framework, setFramework] = useState<AccountingFramework>(props.initialFramework);
  const [nameError, setNameError] = useState<string | null>(null);
  // Guard local de doble-submit (B14): no depende de mutation.isPending para
  // cerrar la ventana entre el primer click y el cambio de estado de TanStack.
  const inFlight = useRef(false);
  const [, forceTick] = useState(0);

  // Sync state cuando cambia la cuenta (switcher) o llega data fresca de /me.
  useEffect(() => {
    setName(props.initialName);
    setCountryCode(props.initialCountry);
    setFramework(props.initialFramework);
  }, [props.initialName, props.initialCountry, props.initialFramework]);

  const isDirty =
    name !== props.initialName ||
    countryCode !== props.initialCountry ||
    framework !== props.initialFramework;
  const submitDisabled = !isDirty || mutation.isPending || inFlight.current;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current || submitDisabled) return; // bloquea doble-click (B14)
    inFlight.current = true;
    forceTick((n) => n + 1); // re-render para reflejar disabled en el botón
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("El nombre es obligatorio.");
      return;
    }
    if (trimmed.length > 100) {
      setNameError("Máximo 100 caracteres.");
      return;
    }
    setNameError(null);
    try {
      await mutation.mutateAsync({
        ...(name !== props.initialName ? { name: trimmed } : {}),
        ...(countryCode !== props.initialCountry ? { countryCode } : {}),
        ...(framework !== props.initialFramework ? { accountingFramework: framework } : {}),
      });
      toast.success("Ajustes guardados");
    } catch (err) {
      if (err instanceof ApiError && err.code === "VALIDATION_ERROR") {
        toast.error("Revisá los datos del formulario.");
      } else {
        toast.error("No se pudo guardar. Intentá de nuevo.");
      }
    } finally {
      inFlight.current = false;
      forceTick((n) => n + 1);
    }
  }

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-app-ink-1 text-base">Datos de la finca</h2>
          <p className="mt-0.5 text-app-ink-3 text-xs">
            Nombre legal, país y marco contable. {!props.editable && "Solo el dueño puede editar."}
          </p>
        </div>
        {!props.editable && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-app-surface-2 px-2 py-0.5 text-app-ink-3 text-xs"
            aria-label="Solo lectura"
          >
            <Lock className="h-3 w-3" />
            Solo lectura
          </span>
        )}
      </header>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <Field id="meta-name" label="Nombre de la finca / organización">
          <input
            id="meta-name"
            type="text"
            maxLength={100}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            disabled={!props.editable || mutation.isPending}
            aria-invalid={Boolean(nameError)}
            className="h-11 w-full rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base focus:border-app-ink-2 focus:outline-none disabled:bg-app-surface-2 disabled:text-app-ink-2"
          />
          {nameError && (
            <p role="alert" className="mt-1 text-rose-600 text-xs">
              {nameError}
            </p>
          )}
        </Field>

        <Field id="meta-country" label="País">
          <input
            id="meta-country"
            type="text"
            maxLength={2}
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
            disabled={!props.editable || mutation.isPending}
            className="h-11 w-24 rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base uppercase focus:border-app-ink-2 focus:outline-none disabled:bg-app-surface-2 disabled:text-app-ink-2"
          />
          <p className="mt-1 text-app-ink-3 text-xs">
            Código ISO (CR, PA…). El timezone sigue fijo en Costa Rica — multi-país completo es
            trabajo futuro.
          </p>
        </Field>

        <fieldset className="flex flex-col gap-2">
          <legend className="font-medium text-app-ink-2 text-sm">Marco contable</legend>
          {FRAMEWORK_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border border-app-border p-3 ${
                framework === opt.value ? "bg-app-surface-2" : ""
              } ${!props.editable ? "cursor-not-allowed opacity-70" : ""}`}
            >
              <input
                type="radio"
                name="meta-framework"
                value={opt.value}
                checked={framework === opt.value}
                onChange={() => setFramework(opt.value)}
                disabled={!props.editable || mutation.isPending}
                className="mt-0.5"
              />
              <div>
                <div className="text-app-ink-1 text-sm">{opt.label}</div>
                <div className="text-app-ink-3 text-xs">{opt.hint}</div>
              </div>
            </label>
          ))}
        </fieldset>

        {props.editable && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitDisabled}
              aria-disabled={submitDisabled}
              className="rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}

// ─── Card 3: Defaults de depreciación (PATCH /:accountId/defaults) ───────────

function DefaultsCard(props: {
  accountId: string;
  initialUsefulLife: number;
  initialResidualRatio: number;
  editable: boolean;
}) {
  const mutation = useUpdateAccountDefaults(props.accountId);
  const [usefulLife, setUsefulLife] = useState(String(props.initialUsefulLife));
  // Internal state como % (0-99), convertimos a fracción [0,1) al enviar.
  const [residualPct, setResidualPct] = useState(
    (props.initialResidualRatio * 100).toFixed(2).replace(/\.?0+$/, ""),
  );
  const [residualError, setResidualError] = useState<string | null>(null);
  const [usefulError, setUsefulError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const [, forceTick] = useState(0);

  useEffect(() => {
    setUsefulLife(String(props.initialUsefulLife));
    setResidualPct((props.initialResidualRatio * 100).toFixed(2).replace(/\.?0+$/, ""));
  }, [props.initialUsefulLife, props.initialResidualRatio]);

  const usefulLifeNum = Number.parseInt(usefulLife, 10);
  const residualNum = Number.parseFloat(residualPct);
  const residualFrac = Number.isFinite(residualNum) ? residualNum / 100 : Number.NaN;

  const isDirty =
    usefulLifeNum !== props.initialUsefulLife ||
    Math.abs(residualFrac - props.initialResidualRatio) > 1e-6;
  const submitDisabled = !isDirty || mutation.isPending || inFlight.current;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current || submitDisabled) return; // doble-click guard (B14)

    let hasError = false;
    if (!Number.isInteger(usefulLifeNum) || usefulLifeNum < 1 || usefulLifeNum > 600) {
      setUsefulError("Debe ser entre 1 y 600 meses.");
      hasError = true;
    } else {
      setUsefulError(null);
    }
    if (!Number.isFinite(residualFrac) || residualFrac < 0 || residualFrac >= 1) {
      setResidualError("Debe ser entre 0 % y 99 %.");
      hasError = true;
    } else {
      setResidualError(null);
    }
    if (hasError) return;

    inFlight.current = true;
    forceTick((n) => n + 1);
    try {
      await mutation.mutateAsync({
        ...(usefulLifeNum !== props.initialUsefulLife
          ? { defaultUsefulLifeMonths: usefulLifeNum }
          : {}),
        ...(Math.abs(residualFrac - props.initialResidualRatio) > 1e-6
          ? { defaultResidualValueRatio: residualFrac }
          : {}),
      });
      toast.success("Defaults actualizados");
    } catch (err) {
      if (err instanceof ApiError && err.code === "VALIDATION_ERROR") {
        toast.error("Revisá los valores ingresados.");
      } else {
        toast.error("No se pudo guardar. Intentá de nuevo.");
      }
    } finally {
      inFlight.current = false;
      forceTick((n) => n + 1);
    }
  }

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-app-ink-1 text-base">Defaults de depreciación</h2>
          <p className="mt-0.5 text-app-ink-3 text-xs">
            Vida útil y valor residual que se aplicarán a animales <strong>nuevos</strong> al pasar
            primer parto. Animales ya productivos conservan los valores con los que se registraron —
            para corregir, usar “Corregir evento” en su primer parto.
          </p>
        </div>
        {!props.editable && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-app-surface-2 px-2 py-0.5 text-app-ink-3 text-xs"
            aria-label="Solo lectura"
          >
            <Lock className="h-3 w-3" />
            Solo lectura
          </span>
        )}
      </header>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <Field id="defaults-useful-life" label="Vida útil (meses)">
          <input
            id="defaults-useful-life"
            type="number"
            min={1}
            max={600}
            step={1}
            inputMode="numeric"
            value={usefulLife}
            onChange={(e) => setUsefulLife(e.target.value)}
            disabled={!props.editable || mutation.isPending}
            aria-invalid={Boolean(usefulError)}
            className="h-11 w-32 rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base focus:border-app-ink-2 focus:outline-none disabled:bg-app-surface-2 disabled:text-app-ink-2"
          />
          <p className="mt-1 text-app-ink-3 text-xs">
            Default contable: 60 meses (estimación NIIF). Fiscal (RISR): 7 años al 15% desde los 3
            años de edad — subí a 84 si querés alinear contable con fiscal.
          </p>
          {usefulError && (
            <p role="alert" className="mt-1 text-rose-600 text-xs">
              {usefulError}
            </p>
          )}
        </Field>

        <Field id="defaults-residual" label="Valor residual (%)">
          <div className="flex items-center gap-2">
            <input
              id="defaults-residual"
              type="number"
              min={0}
              max={99.99}
              step={0.01}
              inputMode="decimal"
              value={residualPct}
              onChange={(e) => setResidualPct(e.target.value)}
              disabled={!props.editable || mutation.isPending}
              aria-invalid={Boolean(residualError)}
              className="h-11 w-32 rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base focus:border-app-ink-2 focus:outline-none disabled:bg-app-surface-2 disabled:text-app-ink-2"
            />
            <span className="text-app-ink-2 text-sm">%</span>
          </div>
          <p className="mt-1 text-app-ink-3 text-xs">
            Fracción del costo acumulado al primer parto que NO se deprecia. Estimación contable
            (default 20 %, rango real CR 10-25%); el fiscal (RISR) no contempla residual.
          </p>
          {residualError && (
            <p role="alert" className="mt-1 text-rose-600 text-xs">
              {residualError}
            </p>
          )}
        </Field>

        {props.editable && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitDisabled}
              aria-disabled={submitDisabled}
              className="rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {mutation.isPending ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="font-medium text-app-ink-2 text-sm">
        {label}
      </label>
      {children}
    </div>
  );
}

function SkeletonAjustes() {
  // 4 secciones: MetaCard + DefaultsCard + ChartCard + MappingsCard.
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 md:px-6">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-48 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
        />
      ))}
    </div>
  );
}
